import React, {useState, useEffect} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as GiIcons from 'react-icons/gi';
import * as FaIcons from 'react-icons/fa';
import {racoaptereto} from '../data/racoaptereto';
import LLMSelector from './LLMSelector';

const {FiCopy, FiCheck, FiShare2, FiEdit3, FiThumbsUp, FiThumbsDown, FiRefreshCw, FiChevronLeft, FiChevronRight} = FiIcons;
const {GiMagicWand} = GiIcons;

const PromptDisplay = ({prompt, onEdit, promptData}) => {
  const [copied, setCopied] = useState(false);
  const [llmCopied, setLlmCopied] = useState(false);
  const [rating, setRating] = useState(null);
  const [isImproving, setIsImproving] = useState(false);
  const [improvedPrompt, setImprovedPrompt] = useState('');
  const [improvedElements, setImprovedElements] = useState({});
  const [improvementHistory, setImprovementHistory] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(0);
  const [showLLMSelector, setShowLLMSelector] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const copyToClipboard = async (textToCopy) => {
    try {
      await navigator.clipboard.writeText(textToCopy || improvedPrompt || prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar texto: ', err);
    }
  };

  const copyToClipboardWithPrefix = async (textToCopy) => {
    try {
      const prefix = "Actúa como experto en ingeniería de instrucciones (prompt engineering). Ayúdame a mejorar el siguiente prompt: ";
      const fullText = prefix + (textToCopy || improvedPrompt || prompt);
      await navigator.clipboard.writeText(fullText);
      setLlmCopied(true);
      setTimeout(() => setLlmCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar texto con prefijo: ', err);
    }
  };

  const sharePrompt = async (textToShare) => {
    const textToShareFinal = textToShare || improvedPrompt || prompt;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Prompt RACOAPTERETO',
          text: textToShareFinal,
        });
      } catch (err) {
        console.error('Error al compartir:', err);
      }
    } else {
      copyToClipboard(textToShareFinal);
    }
  };

  // Función para enviar retroalimentación
  const sendFeedback = (isPositive) => {
    // Preparar el mensaje de retroalimentación
    const feedbackType = isPositive ? "positiva" : "negativa";
    const promptText = improvedPrompt || prompt;
    const userFeedback = {
      type: feedbackType,
      prompt: promptText,
      timestamp: new Date().toISOString()
    };

    // Simular envío de correo (en producción esto sería una API real)
    console.log(`Enviando retroalimentación ${feedbackType}:`, userFeedback);

    // Mostrar mensaje de confirmación
    setFeedbackSent(true);
    setFeedbackMessage(isPositive 
      ? "¡Gracias por tu retroalimentación positiva!" 
      : "Gracias por tu retroalimentación. Trabajaremos para mejorar la herramienta.");
    
    setTimeout(() => {
      setFeedbackSent(false);
      setFeedbackMessage('');
    }, 3000);
  };

  // Función para aplicar reglas gramaticales según la RAE
  const applyGrammarRules = (text) => {
    if (!text) return '';
    
    // Normalizar espacios
    let processed = text.trim().replace(/\s+/g, ' ');
    
    // Asegurar que comienza con mayúscula
    processed = processed.charAt(0).toUpperCase() + processed.slice(1);
    
    return processed;
  };

  // Función para determinar si una palabra debe ir en mayúscula después de un signo de puntuación
  const shouldCapitalize = (previousChar) => {
    // Después de punto o signos de interrogación/exclamación se usa mayúscula
    return ['.', '!', '?'].includes(previousChar);
  };

  // Función para determinar si se necesita una coma antes de un gerundio
  const needsCommaBeforeGerund = (text, position) => {
    // Buscar palabras terminadas en -ando, -endo, -iendo
    const gerundRegex = /\s(([a-záéíóúüñ]+)(ando|endo|iendo))\b/gi;
    let match;
    let needsComma = false;
    
    while ((match = gerundRegex.exec(text)) !== null) {
      // Si el gerundio está después de la posición actual y no hay coma antes
      if (match.index > position && text[match.index - 1] !== ',') {
        needsComma = true;
        break;
      }
    }
    
    return needsComma;
  };

  // Función mejorada para normalizar la puntuación del texto con reglas RAE
  const normalizePunctuation = (text) => {
    if (!text) return '';
    
    // Eliminar espacios en blanco al inicio y final
    let normalized = text.trim();
    
    // Asegurar que comienza con mayúscula
    normalized = normalized.charAt(0).toUpperCase() + normalized.slice(1);
    
    // Corregir espacios antes y después de signos de puntuación
    normalized = normalized
      .replace(/\s+([.,;:!?])/g, '$1') // Eliminar espacios antes de signos
      .replace(/([.,;:!?])(?=[a-záéíóúüñA-ZÁÉÍÓÚÜÑ])/gi, '$1 '); // Añadir espacio después
    
    // Eliminar puntuación al final si existe para aplicar nueva
    normalized = normalized.replace(/[.,;:!?]$/, '');
    
    return normalized;
  };

  // Función para determinar la estructura gramatical del elemento
  const analyzeGrammaticalStructure = (text) => {
    // Detectar si contiene gerundios
    const hasGerund = /\b([a-záéíóúüñ]+)(ando|endo|iendo)\b/i.test(text);
    
    // Detectar si contiene conjunciones coordinantes
    const hasCoordinatingConjunction = /\b(y|e|ni|o|u|pero|sino|aunque|mas)\b/i.test(text);
    
    // Detectar si contiene verbos en infinitivo al inicio
    const startsWithInfinitive = /^([a-záéíóúüñ]+)(ar|er|ir)\b/i.test(text);
    
    // Detectar si contiene subordinadas con "que"
    const hasSubordinateClause = /\b(que|quien|cuyo|cuya|cuyos|cuyas|donde|cuando|como)\b/i.test(text);
    
    return {
      hasGerund,
      hasCoordinatingConjunction,
      startsWithInfinitive,
      hasSubordinateClause
    };
  };

  // Función para determinar el separador apropiado entre elementos con reglas RAE
  const getAppropiateSeparator = (index, totalItems, elementType, currentText, nextText) => {
    const isLastItem = index === totalItems - 1;
    
    if (isLastItem) return '.';
    
    // Analizar la estructura gramatical del texto actual y siguiente
    const currentStructure = analyzeGrammaticalStructure(currentText);
    const nextStructure = nextText ? analyzeGrammaticalStructure(nextText) : null;
    
    // Reglas específicas según el tipo de elemento RACOAPTERETO
    const elementRules = {
      // Elementos que típicamente terminan en punto
      rol: () => {
        if (currentStructure.hasGerund) return ',';
        if (currentStructure.hasSubordinateClause) return ',';
        return '. ';
      },
      
      // Elementos de acción suelen requerir coma antes de gerundio
      accion: () => {
        if (nextStructure && nextStructure.startsWithInfinitive) return ',';
        if (currentStructure.hasSubordinateClause) return ',';
        return currentStructure.hasCoordinatingConjunction ? ' ' : '. ';
      },
      
      // Contexto suele usar punto y coma para separar del objetivo
      contexto: () => {
        if (nextText && nextText.toLowerCase().startsWith('para')) return ',';
        if (nextText && (
          nextText.toLowerCase().startsWith('con el fin') || 
          nextText.toLowerCase().startsWith('con la finalidad') || 
          nextText.toLowerCase().startsWith('con el objetivo') || 
          nextText.toLowerCase().startsWith('con el propósito')
        )) return ',';
        return '. ';
      },
      
      objetivo: () => {
        if (nextStructure && nextStructure.startsWithInfinitive) return '. ';
        if (nextText && (nextText.toLowerCase().includes('dirigido') || nextText.toLowerCase().includes('enfocado'))) return '.';
        return ',';
      },
      
      // Audiencia suele conectarse con la plataforma o tarea mediante punto
      audiencia: () => {
        if (nextText && nextText.toLowerCase().includes('para publicar')) return '.';
        if (nextText && nextText.toLowerCase().includes('en la plataforma')) return '.';
        return '. ';
      },
      
      // Plataforma suele conectarse con la tarea mediante punto
      plataforma: () => '. ',
      
      // Tarea suele conectarse con el ejemplo mediante punto
      tarea: () => '. ',
      
      ejemplo: () => '. ',
      
      resultado: () => '. ',
      
      // Estilo suele conectarse con el tono mediante punto
      estilo: () => '. ',
      
      // Tono suele ser la última parte antes de las opciones
      tono: () => '. ',
      
      // Opciones suelen usar punto y aparte
      opciones: () => '.'
    };
    
    // Si hay una regla específica para este elemento, úsala
    if (elementRules[elementType]) {
      return elementRules[elementType]();
    }
    
    // Regla por defecto: si es el último elemento, punto; si no, punto y coma
    return isLastItem ? '.' : '. ';
  };

  // Función para corregir el uso de mayúsculas después de signos de puntuación
  const correctCapitalization = (text) => {
    // Dividir el texto en fragmentos basados en puntuación
    const fragments = text.split(/([.;!?])/);
    let result = '';
    
    for (let i = 0; i < fragments.length; i++) {
      // Si es un signo de puntuación
      if (['.', ';', '!', '?'].includes(fragments[i])) {
        result += fragments[i];
        
        // Si hay un fragmento después del signo
        if (i + 1 < fragments.length) {
          const nextFragment = fragments[i + 1].trim();
          
          // Si debe ir en mayúscula (después de punto, ! o ?)
          if (shouldCapitalize(fragments[i])) {
            if (nextFragment) {
              result += ' ' + nextFragment.charAt(0).toUpperCase() + nextFragment.slice(1);
            }
          } else {
            // Después de punto y coma, usar minúscula
            if (nextFragment) {
              result += ' ' + nextFragment.charAt(0).toLowerCase() + nextFragment.slice(1);
            }
          }
          
          i++; // Saltar el fragmento ya procesado
        }
      } else {
        result += fragments[i];
      }
    }
    
    return result;
  };

  // Función para añadir comas antes de gerundios
  const addCommasBeforeGerunds = (text) => {
    // Patrón para detectar gerundios no precedidos por coma
    const gerundPattern = /([^,])\s+([a-záéíóúüñ]+(?:ando|iendo|endo)\b)/gi;
    
    // Añadir coma antes del gerundio
    return text.replace(gerundPattern, '$1, $2');
  };

  // Función para añadir comas antes de "para" cuando introduce finalidad
  const addCommasBeforePara = (text) => {
    // Patrón para detectar "para" no precedido por coma cuando introduce finalidad
    const paraPattern = /([^,;.:])\s+(para\s+(?:[a-záéíóúüñ]+(?:ar|er|ir)|que))/gi;
    
    // Añadir coma antes de "para"
    return text.replace(paraPattern, '$1, $2');
  };

  // Función para añadir comas antes de conectores causales
  const addCommasBeforeCausalConnectors = (text) => {
    // Patrones para detectar conectores causales no precedidos por coma
    const causalPattern = /([^,;.:])\s+(ya que|puesto que|dado que|debido a que|porque|pues)\b/gi;
    
    // Añadir coma antes del conector
    return text.replace(causalPattern, '$1, $2');
  };

  // Función para añadir comas antes de conectores consecutivos
  const addCommasBeforeConsecutiveConnectors = (text) => {
    // Patrones para detectar conectores consecutivos no precedidos por coma
    const consecutivePattern = /([^,;.:])\s+(por (lo que|tanto|consiguiente|ende)|así que|de (modo|manera|forma) que)\b/gi;
    
    // Añadir coma antes del conector
    return text.replace(consecutivePattern, '$1, $2');
  };

  // Función para añadir comas antes de conectores concesivos
  const addCommasBeforeConcessive = (text) => {
    // Patrones para detectar conectores concesivos no precedidos por coma
    const concessivePattern = /([^,;.:])\s+(aunque|a pesar de que|si bien|aun cuando|pese a que)\b/gi;
    
    // Añadir coma antes del conector
    return text.replace(concessivePattern, '$1, $2');
  };

  // Función para corregir uso de comas en enumeraciones
  const correctEnumerationCommas = (text) => {
    // Patrón para detectar enumeraciones sin coma
    const enumerationPattern = /([a-záéíóúüñA-ZÁÉÍÓÚÜÑ])\s+(y|e|o|u|ni)\s+([a-záéíóúüñA-ZÁÉÍÓÚÜÑ])/gi;
    
    // No añadir coma antes de la última conjunción en una enumeración
    return text;
  };

  // Función para corregir signos de puntuación repetidos
  const correctRepeatedPunctuation = (text) => {
    // Patrón para detectar signos de puntuación repetidos
    return text.replace(/([.,;:!?])[.,;:!?]+/g, '$1');
  };

  // Función para mejorar la puntuación según reglas RAE
  const improveRAEPunctuation = (text) => {
    if (!text) return '';
    
    let improved = text;
    
    // 1. Normalizar espacios
    improved = improved.trim().replace(/\s+/g, ' ');
    
    // 2. Añadir comas antes de gerundios
    improved = addCommasBeforeGerunds(improved);
    
    // 3. Añadir comas antes de "para" cuando introduce finalidad
    improved = addCommasBeforePara(improved);
    
    // 4. Añadir comas antes de conectores causales
    improved = addCommasBeforeCausalConnectors(improved);
    
    // 5. Añadir comas antes de conectores consecutivos
    improved = addCommasBeforeConsecutiveConnectors(improved);
    
    // 6. Añadir comas antes de conectores concesivos
    improved = addCommasBeforeConcessive(improved);
    
    // 7. Corregir comas en enumeraciones
    improved = correctEnumerationCommas(improved);
    
    // 8. Corregir signos de puntuación repetidos
    improved = correctRepeatedPunctuation(improved);
    
    // 9. Corregir uso de mayúsculas después de puntuación
    improved = correctCapitalization(improved);
    
    // 10. Corregir espacios alrededor de signos de puntuación
    improved = improved
      .replace(/\s+([.,;:!?])/g, '$1') // Eliminar espacios antes de signos
      .replace(/([.,;:!?])(?=[a-záéíóúüñA-ZÁÉÍÓÚÜÑ])/gi, '$1 '); // Añadir espacio después
    
    return improved;
  };

  // Diccionario de sinónimos ampliado
  const synonymsDictionary = {
    'amplia': ['extensa', 'vasta', 'dilatada', 'profunda', 'considerable', 'nutrida', 'copiosa', 'abundante', 'cuantiosa', 'significativa', 'sólida', 'completa', 'exhaustiva', 'integral', 'sustancial', 'voluminosa', 'prolija', 'detallada', 'minuciosa', 'abarcadora', 'comprensiva', 'polifacética', 'diversificada', 'multidisciplinaria', 'heterogénea', 'variada', 'plural', 'multiforme', 'polivalente', 'global'],
    'experiencia': ['trayectoria', 'recorrido', 'bagaje', 'pericia', 'práctica', 'conocimiento', 'maestría', 'destreza', 'aptitud', 'competencia', 'habilidad', 'saber', 'veteranía', 'conocimientos especializados', 'recorrido profesional', 'historial', 'capacitación', 'formación', 'dominio', 'especialización', 'cualificación', 'entrenamiento', 'idoneidad', 'preparación', 'vivencia', 'familiaridad', 'sabiduría', 'praxis', 'oficio', 'manejo'],
    'sector': ['ámbito', 'campo', 'industria', 'área', 'terreno', 'dominio', 'especialidad', 'segmento', 'ramo', 'esfera', 'nicho', 'disciplina', 'materia', 'rama', 'jurisdicción', 'parcela', 'división', 'categoría', 'departamento', 'circunscripción', 'competencia', 'contexto', 'dimensión', 'entorno', 'escenario', 'marco', 'panorama', 'territorio', 'vertiente', 'zona'],
    'reconocido': ['distinguido', 'destacado', 'acreditado', 'prestigioso', 'célebre', 'afamado', 'reputado', 'laureado', 'notable', 'eminente', 'ilustre', 'consagrado', 'respetado', 'admirado', 'galardonado', 'insigne', 'renombrado', 'elogiado', 'aplaudido', 'venerado', 'estimado', 'valorado', 'condecorado', 'aclamado', 'famoso', 'prominente', 'sobresaliente', 'relevante', 'significativo', 'influyente'],
    'resultados': ['logros', 'éxitos', 'consecuciones', 'efectos', 'rendimientos', 'frutos', 'beneficios', 'conquistas', 'productos', 'desenlaces', 'derivaciones', 'secuelas', 'conclusiones', 'consecuencias', 'hallazgos', 'alcances', 'metas', 'objetivos', 'fines', 'propósitos', 'resoluciones', 'determinaciones', 'soluciones', 'rendimientos', 'ganancias', 'utilidades', 'provechos', 'rentabilidades', 'réditos', 'rendición'],
    'excepcionales': ['sobresalientes', 'extraordinarios', 'notables', 'destacados', 'superiores', 'brillantes', 'magistrales', 'prodigiosos', 'excelentes', 'espectaculares', 'admirables', 'asombrosos', 'formidables', 'magníficos', 'sublimes', 'impresionantes', 'deslumbrantes', 'fabulosos', 'portentosos', 'maravillosos', 'insuperables', 'inigualables', 'incomparables', 'únicos', 'singulares', 'insólitos', 'eminentes', 'distinguidos', 'relevantes', 'trascendentales'],
    'mejores': ['óptimas', 'excelentes', 'superiores', 'destacadas', 'sobresalientes', 'extraordinarias', 'ejemplares', 'estelares', 'magníficas', 'idóneas', 'excepcionales', 'selectas', 'escogidas', 'privilegiadas', 'ventajosas', 'favorables', 'adecuadas', 'apropiadas', 'convenientes', 'perfectas', 'insuperables', 'inmejorables', 'incomparables', 'ejemplares', 'paradigmáticas', 'modélicas', 'referentes', 'insignes', 'brillantes', 'eminentes'],
    'prácticas': ['métodos', 'técnicas', 'procedimientos', 'estrategias', 'enfoques', 'sistemas', 'mecanismos', 'operaciones', 'metodologías', 'procesos', 'protocolos', 'rutinas', 'pautas', 'directrices', 'guías', 'instrucciones', 'reglas', 'normas', 'principios', 'tácticas', 'maniobras', 'modos', 'fórmulas', 'recetas', 'prescripciones', 'preceptos', 'cánones', 'criterios', 'estándares', 'lineamientos'],
    'metodologías': ['sistemas', 'procedimientos', 'enfoques', 'técnicas', 'métodos', 'protocolos', 'modelos', 'esquemas', 'planes', 'estructuras', 'frameworks', 'paradigmas', 'aproximaciones', 'perspectivas', 'ópticas', 'tratamientos', 'abordajes', 'vías', 'caminos', 'rutas', 'directrices', 'lineamientos', 'guías', 'manuales', 'compendios', 'estándares', 'patrones', 'normas', 'reglas', 'principios'],
    'actuales': ['vigentes', 'contemporáneas', 'presentes', 'modernas', 'recientes', 'novedosas', 'innovadoras', 'imperantes', 'reinantes', 'prevalecientes', 'existentes', 'corrientes', 'imperantes', 'dominantes', 'habituales', 'usuales', 'frecuentes', 'comunes', 'ordinarias', 'normales', 'regulares', 'típicas', 'características', 'representativas', 'distintivas', 'emblemáticas', 'icónicas', 'significativas', 'relevantes', 'trascendentes'],
    'tendencias': ['corrientes', 'movimientos', 'direcciones', 'inclinaciones', 'orientaciones', 'propensiones', 'modas', 'patrones', 'evoluciones', 'dinámicas', 'derivas', 'rumbos', 'trayectorias', 'vías', 'caminos', 'rutas', 'sendas', 'trazados', 'itinerarios', 'recorridos', 'desarrollos', 'progresos', 'avances', 'adelantos', 'innovaciones', 'novedades', 'vanguardias', 'modernidades', 'actualidades', 'contemporaneidades'],
    'factores': ['elementos', 'componentes', 'variables', 'aspectos', 'condicionantes', 'ingredientes', 'determinantes', 'circunstancias', 'parámetros', 'indicadores', 'causas', 'motivos', 'razones', 'fundamentos', 'bases', 'pilares', 'cimientos', 'soportes', 'apoyos', 'respaldos', 'consideraciones', 'criterios', 'juicios', 'valoraciones', 'evaluaciones', 'apreciaciones', 'estimaciones', 'ponderaciones', 'exámenes', 'análisis'],
    'competitivos': ['rivales', 'concurrentes', 'contendientes', 'adversarios', 'opositores', 'competidores', 'contrincantes', 'antagonistas', 'retadores', 'litigantes', 'contendores', 'oponentes', 'participantes', 'concursantes', 'aspirantes', 'candidatos', 'pretendientes', 'solicitantes', 'demandantes', 'postulantes', 'finalistas', 'selectos', 'elegidos', 'escogidos', 'designados', 'nombrados', 'señalados', 'indicados', 'apuntados', 'referidos'],
    'mercado': ['comercio', 'negocio', 'industria', 'sector', 'ámbito', 'plaza', 'emporio', 'esfera', 'economía', 'campo', 'terreno', 'territorio', 'espacio', 'área', 'zona', 'región', 'localidad', 'ubicación', 'emplazamiento', 'situación', 'posición', 'colocación', 'disposición', 'ordenación', 'organización', 'estructuración', 'configuración', 'formación', 'composición', 'constitución'],
    'audiencia': ['público', 'espectadores', 'oyentes', 'destinatarios', 'receptores', 'seguidores', 'consumidores', 'usuarios', 'observadores', 'concurrencia', 'asistentes', 'participantes', 'invitados', 'congregados', 'reunidos', 'agrupados', 'asociados', 'agregados', 'aglomerados', 'concentrados', 'acumulados', 'amontonados', 'apilados', 'aglutinados', 'conglomerados', 'congregados', 'compactados', 'condensados', 'apretados', 'estrechados'],
    'exigente': ['demandante', 'rigurosa', 'crítica', 'selectiva', 'estricta', 'meticulosa', 'minuciosa', 'escrupulosa', 'perfeccionista', 'severa', 'rigurosa', 'estricta', 'inflexible', 'rígida', 'firme', 'dura', 'férrea', 'implacable', 'inexorable', 'inclemente', 'despiadada', 'cruel', 'brutal', 'feroz', 'salvaje', 'inhumana', 'despiadada', 'inmisericorde', 'inclemente', 'insensible'],
    'soluciones': ['respuestas', 'alternativas', 'opciones', 'resoluciones', 'salidas', 'remedios', 'medidas', 'recursos', 'propuestas', 'planteamientos', 'sugerencias', 'recomendaciones', 'consejos', 'indicaciones', 'instrucciones', 'orientaciones', 'guías', 'directrices', 'pautas', 'normas', 'reglas', 'leyes', 'preceptos', 'mandatos', 'órdenes', 'decretos', 'edictos', 'estatutos', 'reglamentos', 'ordenanzas'],
    'calidad': ['excelencia', 'nivel', 'categoría', 'valor', 'mérito', 'cualidad', 'condición', 'naturaleza', 'virtud', 'propiedad', 'atributo', 'característica', 'rasgo', 'aspecto', 'faceta', 'vertiente', 'dimensión', 'lado', 'cara', 'perfil', 'contorno', 'silueta', 'figura', 'forma', 'configuración', 'estructura', 'organización', 'disposición', 'ordenación', 'arreglo'],
    'aumentar': ['incrementar', 'acrecentar', 'potenciar', 'elevar', 'intensificar', 'ampliar', 'multiplicar', 'extender', 'dilatar', 'expandir', 'engrandecer', 'agrandar', 'ensanchar', 'ensalzar', 'exaltar', 'magnificar', 'enaltecer', 'encomiar', 'elogiar', 'alabar', 'loar', 'aplaudir', 'ovacionar', 'aclamar', 'celebrar', 'festejar', 'conmemorar', 'solemnizar', 'honrar', 'venerar'],
    'significativamente': ['considerablemente', 'notablemente', 'sustancialmente', 'apreciablemente', 'marcadamente', 'sensiblemente', 'ostensiblemente', 'pronunciadamente', 'palpablemente', 'tangiblemente', 'visiblemente', 'evidentemente', 'claramente', 'manifiestamente', 'patentemente', 'notoriamente', 'obviamente', 'indudablemente', 'incuestionablemente', 'indiscutiblemente', 'innegablemente', 'irrefutablemente', 'incontrovertiblemente', 'irrebatiblemente', 'indubitablemente', 'infaliblemente', 'inequívocamente', 'ineluctablemente', 'ineludiblemente', 'irremediablemente'],
    'plazo': ['periodo', 'término', 'lapso', 'intervalo', 'tiempo', 'duración', 'temporada', 'ciclo', 'etapa', 'fase', 'estadio', 'momento', 'instante', 'segundo', 'minuto', 'hora', 'día', 'semana', 'mes', 'año', 'década', 'siglo', 'milenio', 'era', 'época', 'edad', 'generación', 'cohorte', 'promoción', 'hornada'],
    'óptimo': ['ideal', 'perfecto', 'excelente', 'inmejorable', 'adecuado', 'idóneo', 'magnífico', 'supremo', 'insuperable', 'extraordinario', 'excepcional', 'sobresaliente', 'destacado', 'notable', 'relevante', 'importante', 'significativo', 'trascendental', 'esencial', 'fundamental', 'crucial', 'vital', 'crítico', 'decisivo', 'determinante', 'condicionante', 'influyente', 'impactante', 'poderoso', 'potente'],
    'proceso': ['procedimiento', 'método', 'sistema', 'mecanismo', 'operación', 'trámite', 'desarrollo', 'evolución', 'curso', 'progresión', 'avance', 'adelanto', 'progreso', 'mejora', 'perfeccionamiento', 'refinamiento', 'pulimiento', 'afinamiento', 'depuración', 'purificación', 'limpieza', 'aseo', 'higiene', 'desinfección', 'esterilización', 'descontaminación', 'depuración', 'filtración', 'tamizado', 'cribado'],
    'estructurado': ['organizado', 'ordenado', 'sistemático', 'metódico', 'planificado', 'regulado', 'configurado', 'articulado', 'dispuesto', 'coordinado', 'armonizado', 'conciliado', 'concertado', 'acordado', 'pactado', 'convenido', 'estipulado', 'fijado', 'establecido', 'determinado', 'decidido', 'resuelto', 'solucionado', 'zanjado', 'liquidado', 'finiquitado', 'concluido', 'terminado', 'acabado', 'finalizado'],
    'sistemático': ['metódico', 'ordenado', 'organizado', 'regular', 'constante', 'disciplinado', 'riguroso', 'estructurado', 'planificado', 'meticuloso', 'cuidadoso', 'detallado', 'minucioso', 'exhaustivo', 'profundo', 'intenso', 'arduo', 'laborioso', 'trabajoso', 'penoso', 'dificultoso', 'complicado', 'complejo', 'intrincado', 'enrevesado', 'enmarañado', 'embrollado', 'confuso', 'oscuro', 'enigmático'],
    'asegurando': ['garantizando', 'certificando', 'respaldando', 'avalando', 'confirmando', 'verificando', 'corroborando', 'aseverando', 'afirmando', 'acreditando', 'atestiguando', 'testificando', 'declarando', 'manifestando', 'expresando', 'formulando', 'articulando', 'pronunciando', 'profiriendo', 'emitiendo', 'expidiendo', 'despachando', 'enviando', 'remitiendo', 'mandando', 'expidiendo', 'cursando', 'dirigiendo', 'encaminando', 'conduciendo'],
    'ejemplo': ['muestra', 'caso', 'modelo', 'ilustración', 'referencia', 'paradigma', 'prototipo', 'espécimen', 'arquetipo', 'exponente', 'representante', 'símbolo', 'emblema', 'icono', 'imagen', 'figura', 'personaje', 'personalidad', 'celebridad', 'famoso', 'estrella', 'astro', 'ídolo', 'mito', 'leyenda', 'dios', 'deidad', 'divinidad', 'semidiós', 'héroe'],
    'demuestra': ['evidencia', 'prueba', 'confirma', 'verifica', 'corrobora', 'constata', 'acredita', 'certifica', 'testifica', 'atestigua', 'avala', 'respalda', 'apoya', 'secunda', 'sustenta', 'sostiene', 'mantiene', 'conserva', 'preserva', 'guarda', 'custodia', 'protege', 'defiende', 'ampara', 'refugia', 'cobija', 'acoge', 'recibe', 'admite', 'acepta'],
    'aplicación': ['uso', 'empleo', 'utilización', 'implementación', 'ejecución', 'práctica', 'manejo', 'operación', 'administración', 'aprovechamiento', 'explotación', 'beneficio', 'provecho', 'ganancia', 'utilidad', 'rendimiento', 'rentabilidad', 'productividad', 'eficiencia', 'eficacia', 'efectividad', 'rendimiento', 'desempeño', 'actuación', 'comportamiento', 'conducta', 'actitud', 'disposición', 'ánimo', 'espíritu'],
    'práctica': ['funcional', 'útil', 'operativa', 'aplicada', 'pragmática', 'realista', 'concreta', 'tangible', 'eficaz', 'provechosa', 'beneficiosa', 'ventajosa', 'favorable', 'propicia', 'conveniente', 'oportuna', 'adecuada', 'apropiada', 'idónea', 'pertinente', 'relevante', 'importante', 'significativa', 'trascendental', 'esencial', 'fundamental', 'crucial', 'vital', 'crítica', 'decisiva'],
    'concepto': ['idea', 'noción', 'pensamiento', 'principio', 'teoría', 'fundamento', 'postulado', 'doctrina', 'proposición', 'tesis', 'hipótesis', 'conjetura', 'suposición', 'presunción', 'sospecha', 'recelo', 'desconfianza', 'duda', 'incertidumbre', 'inseguridad', 'vacilación', 'indecisión', 'irresolución', 'titubeo', 'fluctuación', 'oscilación', 'alternancia', 'variación', 'cambio', 'transformación'],
    'medibles': ['cuantificables', 'mensurables', 'evaluables', 'valorables', 'estimables', 'calibrables', 'calculables', 'determinables', 'computables', 'dimensionables', 'ponderables', 'apreciables', 'perceptibles', 'detectables', 'observables', 'visibles', 'notorios', 'evidentes', 'manifiestos', 'patentes', 'claros', 'transparentes', 'diáfanos', 'límpidos', 'nítidos', 'precisos', 'exactos', 'rigurosos', 'estrictos', 'severos'],
    'cuantificables': ['medibles', 'mensurables', 'evaluables', 'valorables', 'estimables', 'determinables', 'calculables', 'computables', 'dimensionables', 'calibrables', 'ponderables', 'apreciables', 'perceptibles', 'detectables', 'observables', 'visibles', 'notorios', 'evidentes', 'manifiestos', 'patentes', 'claros', 'transparentes', 'diáfanos', 'límpidos', 'nítidos', 'precisos', 'exactos', 'rigurosos', 'estrictos', 'severos'],
    'impacto': ['efecto', 'influencia', 'repercusión', 'consecuencia', 'trascendencia', 'alcance', 'proyección', 'resonancia', 'eco', 'incidencia', 'importancia', 'relevancia', 'significación', 'valor', 'mérito', 'valía', 'dignidad', 'honor', 'gloria', 'fama', 'renombre', 'prestigio', 'reputación', 'celebridad', 'notoriedad', 'popularidad', 'reconocimiento', 'aclamación', 'admiración', 'veneración'],
    'profesional': ['experto', 'competente', 'cualificado', 'especializado', 'capacitado', 'versado', 'perito', 'experimentado', 'diestro', 'hábil', 'apto', 'idóneo', 'adecuado', 'apropiado', 'conveniente', 'oportuno', 'pertinente', 'relevante', 'importante', 'significativo', 'trascendental', 'esencial', 'fundamental', 'crucial', 'vital', 'crítico', 'decisivo', 'determinante', 'condicionante', 'influyente'],
    'visualmente': ['estéticamente', 'gráficamente', 'ópticamente', 'perceptivamente', 'visiblemente', 'plásticamente', 'figurativamente', 'pictóricamente', 'artísticamente', 'icónicamente', 'simbólicamente', 'emblemáticamente', 'representativamente', 'significativamente', 'expresivamente', 'elocuentemente', 'locuazmente', 'verbosamente', 'copiosamente', 'abundantemente', 'profusamente', 'exuberantemente', 'generosamente', 'liberalmente', 'pródicamente', 'espléndidamente', 'magníficamente', 'suntuosamente', 'lujosamente', 'fastuosamente'],
    'atractivo': ['llamativo', 'cautivador', 'seductor', 'encantador', 'fascinante', 'sugestivo', 'atrayente', 'sugerente', 'interesante', 'apetecible', 'deseable', 'codiciable', 'ambicionable', 'anhelable', 'suspirable', 'soñable', 'imaginable', 'concebible', 'pensable', 'ideado', 'creado', 'inventado', 'diseñado', 'planeado', 'proyectado', 'trazado', 'esbozado', 'delineado', 'perfilado', 'bosquejado'],
    'coherencia': ['congruencia', 'consistencia', 'lógica', 'armonía', 'cohesión', 'concordancia', 'correlación', 'correspondencia', 'uniformidad', 'conexión', 'vinculación', 'relación', 'asociación', 'unión', 'enlace', 'nexo', 'vínculo', 'lazo', 'atadura', 'ligadura', 'sujeción', 'fijación', 'anclaje', 'amarre', 'agarre', 'aprehensión', 'asimiento', 'prensión', 'captura', 'apresamiento'],
    'visual': ['gráfica', 'óptica', 'perceptiva', 'visible', 'estética', 'icónica', 'figurativa', 'pictórica', 'ilustrativa', 'representativa', 'simbólica', 'emblemática', 'alegórica', 'metafórica', 'tropológica', 'analógica', 'comparativa', 'contrastiva', 'diferencial', 'distintiva', 'característica', 'peculiar', 'particular', 'singular', 'única', 'especial', 'excepcional', 'extraordinaria', 'notable', 'sobresaliente'],
    'estructural': ['organizativa', 'constructiva', 'arquitectónica', 'constitutiva', 'esquemática', 'fundamental', 'básica', 'elemental', 'esencial', 'primordial', 'principal', 'cardinal', 'capital', 'crucial', 'vital', 'crítica', 'decisiva', 'determinante', 'condicionante', 'influyente', 'impactante', 'poderosa', 'potente', 'fuerte', 'intensa', 'vigorosa', 'enérgica', 'dinámica', 'activa', 'viva'],
    'adaptado': ['ajustado', 'adecuado', 'amoldado', 'acomodado', 'personalizado', 'modificado', 'acondicionado', 'habituado', 'aclimatado', 'armonizado', 'concordado', 'concertado', 'convenido', 'acordado', 'pactado', 'estipulado', 'fijado', 'establecido', 'determinado', 'decidido', 'resuelto', 'solucionado', 'zanjado', 'liquidado', 'finiquitado', 'concluido', 'terminado', 'acabado', 'finalizado', 'consumado'],
    'perfectamente': ['idóneamente', 'óptimamente', 'adecuadamente', 'impecablemente', 'excelentemente', 'magníficamente', 'espléndidamente', 'extraordinariamente', 'admirablemente', 'maravillosamente', 'prodigiosamente', 'milagrosamente', 'asombrosamente', 'sorprendentemente', 'impresionantemente', 'impactantemente', 'contundentemente', 'rotundamente', 'tajantemente', 'categóricamente', 'absolutamente', 'totalmente', 'completamente', 'enteramente', 'plenamente', 'íntegramente', 'cabalmente', 'perfectamente', 'consumadamente', 'acabadamente'],
    'objetivo': ['meta', 'fin', 'propósito', 'finalidad', 'destino', 'blanco', 'diana', 'designio', 'intención', 'aspiración', 'anhelo', 'deseo', 'ambición', 'pretensión', 'expectativa', 'esperanza', 'ilusión', 'sueño', 'quimera', 'utopía', 'fantasía', 'imaginación', 'creatividad', 'inventiva', 'ingenio', 'talento', 'capacidad', 'aptitud', 'facultad', 'competencia'],
    'transmita': ['comunique', 'exprese', 'proyecte', 'refleje', 'manifieste', 'revele', 'exteriorice', 'exhiba', 'difunda', 'propague', 'divulgue', 'publique', 'anuncie', 'proclame', 'declare', 'manifieste', 'exponga', 'presente', 'muestre', 'despliegue', 'despliega', 'desarrolla', 'evoluciona', 'progresa', 'avanza', 'adelanta', 'mejora', 'perfecciona', 'refina', 'pule'],
    'confianza': ['seguridad', 'credibilidad', 'fiabilidad', 'certeza', 'convicción', 'fe', 'garantía', 'confiabilidad', 'entereza', 'aplomo', 'firmeza', 'fortaleza', 'solidez', 'estabilidad', 'equilibrio', 'balance', 'armonía', 'concordancia', 'consonancia', 'correspondencia', 'correlación', 'coordinación', 'sincronización', 'ajuste', 'acoplamiento', 'encaje', 'engranaje', 'articulación', 'conexión', 'vinculación'],
    'autoridad': ['dominio', 'control', 'poder', 'liderazgo', 'conocimientos', 'mando', 'potestad', 'jurisdicción', 'competencia', 'maestría', 'pericia', 'destreza', 'habilidad', 'aptitud', 'capacidad', 'facultad', 'talento', 'ingenio', 'creatividad', 'imaginación', 'fantasía', 'inventiva', 'originalidad', 'singularidad', 'particularidad', 'peculiaridad', 'distintividad', 'especificidad', 'exclusividad', 'unicidad'],
    'alternativas': ['opciones', 'posibilidades', 'elecciones', 'variantes', 'soluciones', 'caminos', 'vías', 'recursos', 'salidas', 'disyuntivas', 'dilemas', 'encrucijadas', 'bifurcaciones', 'ramificaciones', 'derivaciones', 'desviaciones', 'desvíos', 'atajos', 'rodeos', 'circunvalaciones', 'circunnavegaciones', 'circunlocuciones', 'perífrasis', 'rodeos', 'vueltas', 'giros', 'vericuetos', 'recovecos', 'meandros', 'sinuosidades'],
    'viables': ['factibles', 'realizables', 'posibles', 'practicables', 'ejecutables', 'alcanzables', 'asequibles', 'accesibles', 'hacederas', 'operativas', 'funcionales', 'útiles', 'prácticas', 'aprovechables', 'beneficiosas', 'ventajosas', 'favorables', 'propicias', 'convenientes', 'oportunas', 'adecuadas', 'apropiadas', 'idóneas', 'pertinentes', 'relevantes', 'importantes', 'significativas', 'trascendentales', 'esenciales', 'fundamentales'],
    'significativo': ['importante', 'relevante', 'trascendente', 'considerable', 'sustancial', 'notable', 'destacado', 'prominente', 'señalado', 'marcado', 'acentuado', 'pronunciado', 'definido', 'delimitado', 'delineado', 'perfilado', 'esbozado', 'bosquejado', 'dibujado', 'trazado', 'diseñado', 'planeado', 'proyectado', 'ideado', 'concebido', 'imaginado', 'soñado', 'fantaseado', 'inventado', 'creado'],
    'necesidades': ['requerimientos', 'exigencias', 'demandas', 'requisitos', 'carencias', 'menesteres', 'imperativos', 'insuficiencias', 'faltas', 'precisiones', 'urgencias', 'apremios', 'premuras', 'prontitudes', 'celeridades', 'velocidades', 'rapideces', 'prestezas', 'diligencias', 'expediciones', 'agilidades', 'ligerezas', 'prontitudes', 'inmediateces', 'instantaneidades', 'momentaneidades', 'fugacidades', 'efimeridades', 'transitoriedad', 'temporalidades'],
    'específicas': ['concretas', 'particulares', 'determinadas', 'definidas', 'precisas', 'detalladas', 'singulares', 'exclusivas', 'delimitadas', 'distintivas', 'características', 'peculiares', 'propias', 'inherentes', 'intrínsecas', 'consustanciales', 'esenciales', 'fundamentales', 'básicas', 'elementales', 'primordiales', 'principales', 'primarias', 'capitales', 'cardinales', 'cruciales', 'vitales', 'críticas', 'decisivas', 'determinantes'],
    'proyecto': ['plan', 'iniciativa', 'empresa', 'emprendimiento', 'programa', 'propósito', 'designio', 'idea', 'esquema', 'bosquejo', 'esbozo', 'croquis', 'diseño', 'plano', 'maqueta', 'modelo', 'prototipo', 'ejemplar', 'espécimen', 'muestra', 'patrón', 'paradigma', 'arquetipo', 'estereotipo', 'cliché', 'tópico', 'lugar común', 'banalidad', 'trivialidad', 'vulgaridad'],
    'dirigido': ['orientado', 'enfocado', 'destinado', 'encaminado', 'conducido', 'guiado', 'direccionado', 'encauzado', 'canalizado', 'proyectado', 'apuntado', 'señalado', 'indicado', 'referido', 'aludido', 'mencionado', 'citado', 'nombrado', 'denominado', 'designado', 'identificado', 'especificado', 'determinado', 'definido', 'delimitado', 'demarcado', 'acotado', 'circunscrito', 'confinado', 'restringido'],
    'personas': ['individuos', 'sujetos', 'usuarios', 'consumidores', 'público', 'gente', 'colectivo', 'comunidad', 'población', 'destinatarios', 'receptores', 'beneficiarios', 'favorecidos', 'agraciados', 'afortunados', 'dichosos', 'felices', 'contentos', 'satisfechos', 'complacidos', 'gratificados', 'recompensados', 'premiados', 'galardonados', 'laureados', 'condecorados', 'homenajeados', 'agasajados', 'festejados', 'celebrados'],
    'generación': ['grupo etario', 'cohorte demográfica', 'segmento poblacional', 'grupo generacional', 'colectivo', 'promoción', 'camada', 'descendencia', 'estirpe', 'progenie', 'prole', 'cría', 'retoño', 'vástago', 'hijo', 'descendiente', 'sucesor', 'heredero', 'legatario', 'beneficiario', 'destinatario', 'receptor', 'perceptor', 'cobrador', 'recaudador', 'colector', 'recolector', 'cosechador', 'recopilador', 'compilador'],
    'publicar': ['difundir', 'compartir', 'divulgar', 'emitir', 'distribuir', 'propagar', 'diseminar', 'esparcir', 'expandir', 'transmitir', 'comunicar', 'informar', 'notificar', 'advertir', 'avisar', 'alertar', 'prevenir', 'anticipar', 'adelantar', 'predecir', 'pronosticar', 'vaticinar', 'augurar', 'presagiar', 'adivinar', 'profetizar', 'preconizar', 'anunciar', 'proclamar', 'declarar'],
    'red social': ['plataforma digital', 'medio social', 'canal digital', 'espacio virtual', 'comunidad online', 'sitio interactivo', 'entorno social virtual', 'ecosistema digital', 'aplicación social', 'entorno conectivo', 'comunidad en línea', 'espacio de interacción', 'portal de socialización', 'hub de conexiones', 'plataforma de engagement', 'medio interactivo', 'espacio colaborativo', 'entorno participativo', 'ambiente comunitario', 'esfera social digital', 'universo conectado', 'galaxia interactiva', 'cosmos social', 'dimensión comunitaria', 'realidad compartida', 'mundo virtual', 'existencia paralela', 'vida alternativa', 'experiencia simulada', 'aventura digital'],
    'instagram': ['plataforma visual', 'red fotográfica', 'aplicación de imágenes', 'medio visual', 'canal de contenido', 'red de fotografía', 'comunidad de imágenes', 'espacio multimedia', 'plataforma audiovisual', 'aplicación de historias', 'ecosistema de contenido visual', 'hub de creatividad', 'escaparate digital', 'galería virtual', 'exposición fotográfica', 'museo visual contemporáneo', 'exhibición de momentos', 'colección de instantes', 'archivo de memorias', 'banco de recuerdos', 'tesoro de experiencias', 'baúl de vivencias', 'cofre de sensaciones', 'arca de emociones', 'depósito de sentimientos', 'almacén de pasiones', 'reservorio de impresiones', 'repositorio de percepciones', 'compilación de visiones', 'antología de miradas'],
    'plataforma': ['medio', 'canal', 'sitio', 'aplicación', 'espacio virtual', 'entorno', 'sistema', 'interfaz', 'soporte', 'vehículo', 'conducto', 'vía', 'camino', 'ruta', 'senda', 'vereda', 'sendero', 'pista', 'rastro', 'huella', 'vestigio', 'indicio', 'señal', 'marca', 'signo', 'símbolo', 'emblema', 'insignia', 'distintivo', 'característica'],
    'contenido': ['material', 'información', 'datos', 'recursos', 'elementos', 'materia', 'tema', 'asunto', 'sustancia', 'esencia', 'núcleo', 'corazón', 'médula', 'meollo', 'tuétano', 'hueso', 'semilla', 'germen', 'embrión', 'feto', 'criatura', 'bebé', 'infante', 'niño', 'joven', 'adolescente', 'muchacho', 'chico', 'mozo', 'mancebo'],
    'estrategia': ['plan', 'táctica', 'método', 'procedimiento', 'sistema', 'enfoque', 'maniobra', 'esquema', 'diseño', 'programa', 'proyecto', 'iniciativa', 'empresa', 'emprendimiento', 'aventura', 'expedición', 'exploración', 'investigación', 'indagación', 'averiguación', 'pesquisa', 'búsqueda', 'rastreo', 'seguimiento', 'persecución', 'caza', 'captura', 'aprehensión', 'detención', 'arresto'],
    'desarrollar': ['elaborar', 'crear', 'generar', 'producir', 'construir', 'fabricar', 'forjar', 'concebir', 'idear', 'componer', 'formular', 'plantear', 'proponer', 'sugerir', 'recomendar', 'aconsejar', 'indicar', 'señalar', 'apuntar', 'dirigir', 'encaminar', 'guiar', 'conducir', 'llevar', 'transportar', 'trasladar', 'mover', 'desplazar', 'transferir', 'transitar'],
    'implementar': ['ejecutar', 'aplicar', 'poner en práctica', 'instaurar', 'establecer', 'implantar', 'introducir', 'instituir', 'efectuar', 'realizar', 'cumplir', 'consumar', 'culminar', 'coronar', 'rematar', 'finalizar', 'terminar', 'acabar', 'concluir', 'cerrar', 'clausurar', 'liquidar', 'finiquitar', 'zanjar', 'solventar', 'resolver', 'decidir', 'determinar', 'establecer', 'fijar'],
    'analizar': ['examinar', 'estudiar', 'investigar', 'escrutar', 'inspeccionar', 'indagar', 'revisar', 'explorar', 'sondear', 'escudriñar', 'hurgar', 'escarbar', 'cavar', 'excavar', 'ahondar', 'profundizar', 'adentrarse', 'sumergirse', 'zambullirse', 'bucear', 'nadar', 'flotar', 'emerger', 'aflorar', 'surgir', 'brotar', 'germinar', 'florecer', 'fructificar', 'madurar'],
    'interactivo': ['participativo', 'dinámico', 'recíproco', 'comunicativo', 'colaborativo', 'bidireccional', 'dialógico', 'activo', 'reactivo', 'responsivo', 'sensible', 'receptivo', 'perceptivo', 'consciente', 'atento', 'vigilante', 'alerta', 'despierto', 'vivo', 'lúcido', 'clarividente', 'perspicaz', 'sagaz', 'astuto', 'agudo', 'penetrante', 'incisivo', 'mordaz', 'punzante', 'hiriente'],
    'inmersivo': ['envolvente', 'absorbente', 'cautivador', 'atrapante', 'fascinante', 'intenso', 'profundo', 'penetrante', 'sumergente', 'totalizador', 'completo', 'íntegro', 'cabal', 'pleno', 'absoluto', 'total', 'entero', 'global', 'universal', 'cósmico', 'celestial', 'divino', 'supremo', 'excelso', 'sublime', 'magnífico', 'espléndido', 'soberbio', 'grandioso', 'majestuoso'],
    'innovador': ['revolucionario', 'pionero', 'vanguardista', 'original', 'creativo', 'inédito', 'novedoso', 'inventivo', 'disruptivo', 'renovador', 'reformador', 'transformador', 'modificador', 'alterador', 'variador', 'cambiador', 'mutador', 'permutador', 'conmutador', 'intercambiador', 'sustitutivo', 'reemplazante', 'suplente', 'sucesor', 'reemplazo', 'recambio', 'relevo', 'remuda', 'renovación', 'regeneración'],
    'efectivo': ['eficaz', 'eficiente', 'productivo', 'provechoso', 'útil', 'beneficioso', 'ventajoso', 'rendidor', 'funcional', 'operativo', 'práctico', 'pragmático', 'realista', 'sensato', 'juicioso', 'prudente', 'cauto', 'precavido', 'prevenido', 'previsor', 'anticipador', 'adelantado', 'avanzado', 'progresista', 'vanguardista', 'pionero', 'innovador', 'revolucionario', 'radical', 'extremo'],
    
    // Conectores y frases de enlace
    'además': ['asimismo', 'también', 'igualmente', 'del mismo modo', 'de igual manera', 'de igual forma', 'de igual modo', 'paralelamente', 'simultáneamente', 'a la vez', 'al mismo tiempo', 'en paralelo', 'conjuntamente', 'aunadamente', 'adicionalmente', 'complementariamente', 'suplementariamente', 'sumado a esto', 'a esto se añade', 'hay que añadir', 'cabe agregar', 'conviene subrayar', 'es preciso destacar', 'es necesario recalcar', 'es importante enfatizar', 'merece la pena mencionar', 'no hay que olvidar', 'debe considerarse', 'es relevante señalar', 'vale la pena mencionar'],
    'sin embargo': ['no obstante', 'con todo', 'aun así', 'a pesar de todo', 'a pesar de ello', 'pese a todo', 'pese a ello', 'aun así', 'de todas formas', 'de todos modos', 'de todas maneras', 'en cualquier caso', 'en todo caso', 'sea como sea', 'sea como fuere', 'sea lo que fuere', 'sea cual sea', 'sea cual fuere', 'comoquiera que sea', 'como quiera que sea', 'de cualquier forma', 'de cualquier manera', 'de cualquier modo', 'en cualquier supuesto', 'en cualquier circunstancia', 'bajo cualquier condición', 'en toda situación', 'en todo contexto', 'en todo escenario', 'en toda coyuntura'],
    'por lo tanto': ['por consiguiente', 'por ende', 'en consecuencia', 'como resultado', 'por esta razón', 'por este motivo', 'debido a esto', 'a causa de esto', 'como consecuencia', 'consiguientemente', 'consecuentemente', 'de ahí que', 'de donde se sigue', 'de lo que se desprende', 'de lo que se deduce', 'de lo que se infiere', 'de lo que se concluye', 'de lo que se colige', 'de lo que se deriva', 'de lo cual resulta', 'esto implica', 'esto supone', 'esto conlleva', 'esto entraña', 'esto acarrea', 'esto provoca', 'esto ocasiona', 'esto origina', 'esto genera', 'esto produce'],
    'en primer lugar': ['primeramente', 'en primera instancia', 'como punto de partida', 'inicialmente', 'para comenzar', 'para empezar', 'ante todo', 'antes que nada', 'lo primero es', 'como primer paso', 'en un principio', 'de entrada', 'en el inicio', 'al principio', 'al comienzo', 'al inicio', 'en los albores', 'en los inicios', 'en los primeros momentos', 'en la fase inicial', 'en la etapa inicial', 'en el período inicial', 'en el estadio inicial', 'en la primera fase', 'en la primera etapa', 'en el primer período', 'en el primer estadio', 'en la primera instancia', 'en el primer momento', 'en primera línea'],
    'en conclusión': ['para concluir', 'para finalizar', 'para terminar', 'en síntesis', 'en resumen', 'en suma', 'en definitiva', 'a modo de cierre', 'a modo de conclusión', 'a modo de resumen', 'a modo de síntesis', 'a modo de recapitulación', 'a modo de colofón', 'a modo de desenlace', 'a modo de remate', 'como conclusión', 'como cierre', 'como colofón', 'como desenlace', 'como remate', 'como resumen', 'como síntesis', 'como recapitulación', 'como corolario', 'como culminación', 'como término', 'como punto final', 'como broche final', 'como última consideración', 'como reflexión final'],
    'por ejemplo': ['como muestra', 'como ejemplo', 'a modo de ejemplo', 'a modo de ilustración', 'a título ilustrativo', 'a título de ejemplo', 'como caso ilustrativo', 'como caso ejemplar', 'como caso paradigmático', 'como caso representativo', 'como caso típico', 'como caso característico', 'como caso emblemático', 'como caso simbólico', 'como caso significativo', 'como caso relevante', 'como caso destacado', 'como caso notable', 'como caso sobresaliente', 'como caso prominente', 'como caso singular', 'como caso particular', 'como caso especial', 'como caso concreto', 'como caso específico', 'como caso determinado', 'como caso definido', 'como caso preciso', 'como caso exacto', 'como caso puntual'],
    'en relación con': ['con respecto a', 'con referencia a', 'en lo que concierne a', 'en lo que se refiere a', 'en lo que respecta a', 'en lo referente a', 'en lo tocante a', 'en cuanto a', 'acerca de', 'sobre', 'a propósito de', 'en torno a', 'en lo concerniente a', 'en lo relativo a', 'en lo que atañe a', 'en lo que compete a', 'en lo que incumbe a', 'en lo que corresponde a', 'en lo que toca a', 'en lo que afecta a', 'en lo que implica a', 'en lo que involucra a', 'en lo que compromete a', 'en lo que vincula a', 'en lo que relaciona a', 'en lo que conecta a', 'en lo que enlaza a', 'en lo que une a', 'en lo que liga a', 'en lo que ata a'],
    'de acuerdo con': ['según', 'conforme a', 'de conformidad con', 'en consonancia con', 'en concordancia con', 'en armonía con', 'en sintonía con', 'en línea con', 'en consonancia con', 'en conformidad con', 'en correspondencia con', 'en correlación con', 'en concomitancia con', 'en coincidencia con', 'en convergencia con', 'en congruencia con', 'en coherencia con', 'en cohesión con', 'en conexión con', 'en conjunción con', 'en coordinación con', 'en colaboración con', 'en cooperación con', 'en asociación con', 'en alianza con', 'en coalición con', 'en federación con', 'en confederación con', 'en unión con', 'en reunión con'],
    'a pesar de': ['pese a', 'no obstante', 'aun cuando', 'si bien', 'aunque', 'aun cuando', 'a pesar de que', 'pese a que', 'no obstante que', 'aun cuando', 'si bien es cierto que', 'aunque es verdad que', 'aunque es cierto que', 'por más que', 'por mucho que', 'por muy', 'con todo y que', 'con todo lo que', 'a despecho de', 'a despecho de que', 'a contrapelo de', 'a contrapelo de que', 'en contra de', 'en contra de que', 'en oposición a', 'en oposición a que', 'en contradicción con', 'en contradicción con que', 'en contraste con', 'en contraste con que'],
    'con el fin de': ['con el objetivo de', 'con el propósito de', 'con la finalidad de', 'con la intención de', 'con la meta de', 'con la aspiración de', 'con la pretensión de', 'con la ambición de', 'con el anhelo de', 'con el deseo de', 'con la voluntad de', 'con el afán de', 'con el empeño de', 'con el interés de', 'con la motivación de', 'con la determinación de', 'con la resolución de', 'con la decisión de', 'con el compromiso de', 'con la obligación de', 'con el deber de', 'con la responsabilidad de', 'con la encomienda de', 'con la misión de', 'con la tarea de', 'con la labor de', 'con la función de', 'con el rol de', 'con el papel de', 'con la posición de'],
    'debido a': ['a causa de', 'por causa de', 'por motivo de', 'por razón de', 'en virtud de', 'gracias a', 'merced a', 'a consecuencia de', 'como consecuencia de', 'como resultado de', 'como efecto de', 'como producto de', 'como fruto de', 'como derivación de', 'como secuela de', 'como corolario de', 'como inferencia de', 'como deducción de', 'como conclusión de', 'como colofón de', 'como desenlace de', 'como culminación de', 'como término de', 'como fin de', 'como final de', 'como remate de', 'como cierre de', 'como clausura de', 'como terminación de', 'como finalización de']
  };

  // Función para reemplazar palabras repetidas con sinónimos
  const replaceSynonyms = (text) => {
    let improved = text;
    
    // Contar ocurrencias de cada palabra
    const wordCounts = {};
    const words = text.toLowerCase().match(/[a-záéíóúüñ]+/g) || [];
    
    words.forEach(word => {
      if (word.length > 3) { // Ignorar palabras muy cortas
        wordCounts[word] = (wordCounts[word] || 0) + 1;
      }
    });
    
    // Reemplazar palabras repetidas con sinónimos
    Object.keys(wordCounts).forEach(word => {
      if (wordCounts[word] > 1 && synonymsDictionary[word]) {
        // Encontrar todas las ocurrencias excepto la primera
        let count = 0;
        improved = improved.replace(new RegExp(`\\b${word}\\b`, 'gi'), match => {
          count++;
          if (count === 1) return match; // Mantener la primera ocurrencia
          
          // Usar un sinónimo aleatorio para las siguientes ocurrencias
          const synonyms = synonymsDictionary[word];
          const synonym = synonyms[Math.floor(Math.random() * synonyms.length)];
          return match.charAt(0) === match.charAt(0).toUpperCase() 
            ? synonym.charAt(0).toUpperCase() + synonym.slice(1) 
            : synonym;
        });
      }
    });
    
    return improved;
  };

  // Función para aplicar conectores de forma inteligente
  const applySmartConnectors = (text) => {
    // Lista de patrones para mejorar la fluidez con conectores
    const connectorPatterns = [
      // Añadir conectores de adición
      {
        pattern: /(\. )([A-ZÁÉÍÓÚÜÑ][^.!?]*?\b(también|además|asimismo)\b)/g,
        replacement: '. Además, $2'
      },
      // Añadir conectores de contraste
      {
        pattern: /(\. )([A-ZÁÉÍÓÚÜÑ][^.!?]*?\b(pero|aunque|no obstante)\b)/g,
        replacement: '. Sin embargo, $2'
      },
      // Añadir conectores de consecuencia
      {
        pattern: /(\. )([A-ZÁÉÍÓÚÜÑ][^.!?]*?\b(así|entonces|por eso)\b)/g,
        replacement: '. Por lo tanto, $2'
      },
      // Añadir conectores de ejemplificación
      {
        pattern: /(\. )([A-ZÁÉÍÓÚÜÑ][^.!?]*?\b(como|tal como|así como)\b)/g,
        replacement: '. Por ejemplo, $2'
      },
      // Añadir conectores de finalidad
      {
        pattern: /(\. )([A-ZÁÉÍÓÚÜÑ][^.!?]*?\b(para|a fin de|con el objetivo de)\b)/g,
        replacement: '. Con el fin de, $2'
      },
    ];
    
    // Aplicar cada patrón de mejora
    let improved = text;
    connectorPatterns.forEach(({pattern, replacement}) => {
      improved = improved.replace(pattern, replacement);
    });
    
    return improved;
  };

  // Función para mejorar la variedad léxica
  const improveLexicalVariety = (text) => {
    // Reemplazar palabras comunes con alternativas más sofisticadas
    const commonWordReplacements = [
      {
        pattern: /\b(bueno|buen)\b/gi,
        replacements: ['excelente', 'óptimo', 'extraordinario', 'sobresaliente', 'excepcional']
      },
      {
        pattern: /\b(malo)\b/gi,
        replacements: ['deficiente', 'inadecuado', 'insatisfactorio', 'deplorable', 'pésimo']
      },
      {
        pattern: /\b(grande)\b/gi,
        replacements: ['considerable', 'sustancial', 'significativo', 'prominente', 'imponente']
      },
      {
        pattern: /\b(pequeño)\b/gi,
        replacements: ['reducido', 'diminuto', 'exiguo', 'limitado', 'modesto']
      },
      {
        pattern: /\b(importante)\b/gi,
        replacements: ['crucial', 'fundamental', 'esencial', 'trascendental', 'relevante']
      },
      {
        pattern: /\b(hacer)\b/gi,
        replacements: ['realizar', 'ejecutar', 'efectuar', 'llevar a cabo', 'implementar']
      },
      {
        pattern: /\b(decir)\b/gi,
        replacements: ['expresar', 'manifestar', 'comunicar', 'declarar', 'afirmar']
      },
      {
        pattern: /\b(ver)\b/gi,
        replacements: ['observar', 'visualizar', 'contemplar', 'examinar', 'percibir']
      },
      {
        pattern: /\b(usar)\b/gi,
        replacements: ['utilizar', 'emplear', 'aplicar', 'implementar', 'aprovechar']
      },
      {
        pattern: /\b(obtener)\b/gi,
        replacements: ['adquirir', 'conseguir', 'lograr', 'alcanzar', 'procurar']
      },
    ];
    
    let improved = text;
    
    commonWordReplacements.forEach(({pattern, replacements}) => {
      improved = improved.replace(pattern, match => {
        const replacement = replacements[Math.floor(Math.random() * replacements.length)];
        return match.charAt(0) === match.charAt(0).toUpperCase() 
          ? replacement.charAt(0).toUpperCase() + replacement.slice(1) 
          : replacement;
      });
    });
    
    return improved;
  };

  // Generar variaciones de mejora para cada elemento
  const generateElementVariations = (elementKey, originalContent, elementData) => {
    if (!originalContent || !elementData) return [originalContent];
    
    // Normalizar el contenido original primero
    let normalizedContent = normalizePunctuation(originalContent);
    
    // Variaciones por tipo de elemento
    const variations = [];
    
    // Añadir el contenido normalizado como primera variación
    variations.push(normalizedContent);
    
    // Estrategias de mejora específicas por tipo de elemento
    switch(elementKey) {
      case 'rol':
        variations.push(`${normalizedContent} con amplia trayectoria en el campo`);
        variations.push(`${normalizedContent} de reconocido prestigio internacional`);
        variations.push(`${normalizedContent} con formación especializada y experiencia comprobada`);
        variations.push(`${normalizedContent} líder en innovación y desarrollo`);
        variations.push(`${normalizedContent} con perspectiva estratégica y visión global`);
        variations.push(`${normalizedContent} con enfoque orientado a resultados`);
        variations.push(`${normalizedContent} con capacidad analítica avanzada`);
        variations.push(`${normalizedContent} con conocimiento profundo del sector`);
        variations.push(`${normalizedContent} con habilidades excepcionales para la resolución de problemas complejos`);
        variations.push(`${normalizedContent} con vasta experiencia en implementación de soluciones efectivas`);
        variations.push(`${normalizedContent} con destacada reputación en la industria`);
        variations.push(`${normalizedContent} con certificaciones de alto nivel y credenciales verificadas`);
        variations.push(`${normalizedContent} con historial comprobado de éxitos documentados`);
        variations.push(`${normalizedContent} con mentalidad innovadora y visión disruptiva`);
        variations.push(`${normalizedContent} con extensa red de contactos profesionales estratégicos`);
        variations.push(`${normalizedContent} con metodologías probadas y resultados cuantificables`);
        variations.push(`${normalizedContent} con dominio de herramientas tecnológicas de vanguardia`);
        variations.push(`${normalizedContent} con enfoque multidisciplinario y perspectiva holística`);
        variations.push(`${normalizedContent} con competencias avanzadas en gestión de proyectos complejos`);
        variations.push(`${normalizedContent} con reconocimiento por parte de líderes del sector`);
        variations.push(`${normalizedContent} con trayectoria en organizaciones de prestigio internacional`);
        variations.push(`${normalizedContent} con capacidad para anticipar tendencias y adaptar estrategias`);
        variations.push(`${normalizedContent} con aptitudes exce pcionales para la comunicación estratégica`);
        variations.push(`${normalizedContent} con profunda comprensión de las dinámicas del mercado actual`);
        variations.push(`${normalizedContent} con una visión transformadora y capacidad ejecutiva probada`);
        variations.push(`${normalizedContent} con talento para identificar oportunidades inexploradas`);
        variations.push(`${normalizedContent} con destreza para optimizar procesos y maximizar resultados`);
        variations.push(`${normalizedContent} con habilidad para desarrollar estrategias disruptivas efectivas`);
        variations.push(`${normalizedContent} con aptitud natural para el liderazgo transformacional`);
        variations.push(`${normalizedContent} con capacidad demostrada para impulsar innovaciones revolucionarias`);
        break;
        
      case 'accion':
        variations.push(`${normalizedContent}, aplicando metodologías innovadoras`);
        variations.push(`${normalizedContent} mediante un proceso sistemático y estructurado`);
        variations.push(`${normalizedContent} con un enfoque basado en datos y evidencias`);
        variations.push(`${normalizedContent} utilizando herramientas de vanguardia`);
        variations.push(`${normalizedContent} a través de técnicas avanzadas de análisis`);
        variations.push(`${normalizedContent} implementando prácticas óptimas del sector`);
        variations.push(`${normalizedContent} con un método iterativo y adaptativo`);
        variations.push(`${normalizedContent} siguiendo principios de excelencia y precisión`);
        variations.push(`${normalizedContent} incorporando elementos de innovación disruptiva`);
        variations.push(`${normalizedContent}, empleando marcos conceptuales de última generación`);
        variations.push(`${normalizedContent} mediante la integración de perspectivas multidisciplinarias`);
        variations.push(`${normalizedContent}, aplicando principios de diseño centrado en el usuario`);
        variations.push(`${normalizedContent} con un enfoque holístico e integrador`);
        variations.push(`${normalizedContent}, utilizando modelos predictivos avanzados`);
        variations.push(`${normalizedContent} a través de procesos altamente optimizados`);
        variations.push(`${normalizedContent}, implementando estrategias de gestión ágil`);
        variations.push(`${normalizedContent} siguiendo protocolos de calidad internacional`);
        variations.push(`${normalizedContent}, aprovechando sinergias interdepartamentales`);
        variations.push(`${normalizedContent} mediante la aplicación de inteligencia colectiva`);
        variations.push(`${normalizedContent}, combinando enfoques tradicionales y disruptivos`);
        variations.push(`${normalizedContent} con una metodología escalable y adaptable`);
        variations.push(`${normalizedContent}, priorizando la eficiencia y maximización de recursos`);
        variations.push(`${normalizedContent} mediante técnicas de pensamiento lateral y divergente`);
        variations.push(`${normalizedContent} a través de un sistema de mejora continua`);
        variations.push(`${normalizedContent}, implementando ciclos de retroalimentación constante`);
        variations.push(`${normalizedContent} con herramientas de analítica avanzada y procesamiento de datos`);
        variations.push(`${normalizedContent}, aplicando principios de sostenibilidad y responsabilidad`);
        variations.push(`${normalizedContent} mediante la orquestación de recursos especializados`);
        variations.push(`${normalizedContent}, utilizando plataformas tecnológicas integradas`);
        variations.push(`${normalizedContent} a través de la implementación de sistemas inteligentes de gestión`);
        break;
        
      case 'contexto':
        variations.push(`${normalizedContent} en un entorno altamente competitivo y cambiante`);
        variations.push(`${normalizedContent} considerando las tendencias actuales del mercado`);
        variations.push(`${normalizedContent} teniendo en cuenta factores socioeconómicos relevantes`);
        variations.push(`${normalizedContent} frente a los desafíos de la transformación digital`);
        variations.push(`${normalizedContent} en el marco de la sostenibilidad y responsabilidad social`);
        variations.push(`${normalizedContent} ante un panorama de constante evolución tecnológica`);
        variations.push(`${normalizedContent} con especial atención a las dinámicas del sector`);
        variations.push(`${normalizedContent} bajo las condiciones actuales del mercado global`);
        variations.push(`${normalizedContent} considerando el comportamiento y expectativas del consumidor moderno`);
        variations.push(`${normalizedContent} en un ecosistema digital en permanente transformación`);
        variations.push(`${normalizedContent} frente a la creciente demanda de personalización y experiencias únicas`);
        variations.push(`${normalizedContent} en medio de un cambio paradigmático en los hábitos de consumo`);
        variations.push(`${normalizedContent} ante la convergencia de tecnologías emergentes disruptivas`);
        variations.push(`${normalizedContent} considerando las nuevas exigencias regulatorias y normativas`);
        variations.push(`${normalizedContent} en un entorno donde la adaptabilidad determina la supervivencia`);
        variations.push(`${normalizedContent} frente a la aceleración de ciclos de innovación y obsolescencia`);
        variations.push(`${normalizedContent} en el contexto de una economía basada en plataformas digitales`);
        variations.push(`${normalizedContent} considerando las implicaciones éticas de las nuevas tecnologías`);
        variations.push(`${normalizedContent} en un escenario marcado por la hiperpersonalización`);
        variations.push(`${normalizedContent} ante la fragmentación de audiencias y canales de comunicación`);
        variations.push(`${normalizedContent} en un panorama donde la experiencia del usuario es prioritaria`);
        variations.push(`${normalizedContent} frente a la democratización del acceso a tecnologías avanzadas`);
        variations.push(`${normalizedContent} en un entorno caracterizado por la volatilidad y la incertidumbre`);
        variations.push(`${normalizedContent} considerando la transición hacia modelos de negocio circulares`);
        variations.push(`${normalizedContent} ante la creciente importancia de la autenticidad y transparencia`);
        variations.push(`${normalizedContent} en un contexto donde la velocidad de adaptación es crítica`);
        variations.push(`${normalizedContent} frente a la evolución de las expectativas generacionales`);
        variations.push(`${normalizedContent} en un ecosistema interconectado y dependiente de datos`);
        variations.push(`${normalizedContent} considerando los nuevos paradigmas de creación de valor`);
        variations.push(`${normalizedContent} ante la redefinición de las fronteras entre industrias tradicionales`);
        break;
        
      case 'objetivo':
        variations.push(`${normalizedContent} para maximizar el retorno de inversión`);
        variations.push(`${normalizedContent} con metas específicas y cuantificables`);
        variations.push(`${normalizedContent} para consolidar el posicionamiento estratégico`);
        variations.push(`${normalizedContent} buscando un impacto significativo y sostenible`);
        variations.push(`${normalizedContent} con una visión de crecimiento exponencial`);
        variations.push(`${normalizedContent} para transformar positivamente la experiencia del usuario`);
        variations.push(`${normalizedContent} con indicadores clave de rendimiento claramente definidos`);
        variations.push(`${normalizedContent} para establecer un nuevo estándar en la industria`);
        variations.push(`${normalizedContent} con una perspectiva integral de desarrollo`);
        variations.push(`${normalizedContent} para potenciar la diferenciación competitiva en el mercado`);
        variations.push(`${normalizedContent} con métricas de éxito alineadas con la visión corporativa`);
        variations.push(`${normalizedContent} para catalizar la innovación dentro de la organización`);
        variations.push(`${normalizedContent} con un enfoque orientado a resultados tangibles`);
        variations.push(`${normalizedContent} para optimizar procesos clave y eliminar ineficiencias`);
        variations.push(`${normalizedContent} con metas ambiciosas pero alcanzables a corto y medio plazo`);
        variations.push(`${normalizedContent} para impulsar una transformación cultural sostenible`);
        variations.push(`${normalizedContent} con un sistema de evaluación continua y ajuste estratégico`);
        variations.push(`${normalizedContent} para generar ventajas competitivas difícilmente imitables`);
        variations.push(`${normalizedContent} con un enfoque de creación de valor compartido`);
        variations.push(`${normalizedContent} para desarrollar capacidades organizativas distintivas`);
        variations.push(`${normalizedContent} con un horizonte temporal claramente establecido`);
        variations.push(`${normalizedContent} para capitalizar oportunidades emergentes en el mercado`);
        variations.push(`${normalizedContent} con un balance entre objetivos a corto y largo plazo`);
        variations.push(`${normalizedContent} para fomentar la lealtad y retención de clientes`);
        variations.push(`${normalizedContent} con mecanismos de retroalimentación incorporados`);
        variations.push(`${normalizedContent} para construir relaciones duraderas con los stakeholders`);
        variations.push(`${normalizedContent} con un compromiso hacia la excelencia operacional`);
        variations.push(`${normalizedContent} para ampliar el alcance y penetración en nuevos segmentos`);
        variations.push(`${normalizedContent} con una estrategia de escalabilidad planificada`);
        variations.push(`${normalizedContent} para redefinir los paradigmas establecidos en el sector`);
        break;
        
      case 'audiencia':
        variations.push(`${normalizedContent} con intereses específicos en innovación y tecnología`);
        variations.push(`${normalizedContent} caracterizada por su alto nivel adquisitivo y exigencia`);
        variations.push(`${normalizedContent} con valores centrados en la sostenibilidad y conciencia social`);
        variations.push(`${normalizedContent} con hábitos de consumo digital avanzados`);
        variations.push(`${normalizedContent} con necesidades particulares de información especializada`);
        variations.push(`${normalizedContent} perteneciente a entornos profesionales diversos`);
        variations.push(`${normalizedContent} con aspiraciones de crecimiento personal y profesional`);
        variations.push(`${normalizedContent} con preferencia por contenidos de alta calidad y relevancia`);
        variations.push(`${normalizedContent} que valora la autenticidad y transparencia en las comunicaciones`);
        variations.push(`${normalizedContent} con expectativas elevadas respecto a experiencias personalizadas`);
        variations.push(`${normalizedContent} caracterizada por su capacidad de influencia en redes sociales`);
        variations.push(`${normalizedContent} con una mentalidad cosmopolita y visión multicultural`);
        variations.push(`${normalizedContent} que prioriza la relación calidad-valor en sus decisiones`);
        variations.push(`${normalizedContent} con comportamientos de consumo multicanal e integrado`);
        variations.push(`${normalizedContent} que busca conexiones emocionales con las marcas`);
        variations.push(`${normalizedContent} caracterizada por su alto nivel de alfabetización digital`);
        variations.push(`${normalizedContent} con expectativas de respuestas inmediatas y soluciones ágiles`);
        variations.push(`${normalizedContent} que valora las experiencias sobre las posesiones materiales`);
        variations.push(`${normalizedContent} con un enfoque crítico hacia los mensajes publicitarios tradicionales`);
        variations.push(`${normalizedContent} caracterizada por su capacidad para investigar antes de decidir`);
        variations.push(`${normalizedContent} que demanda coherencia entre valores declarados y acciones`);
        variations.push(`${normalizedContent} con interés en participar activamente en la cocreación`);
        variations.push(`${normalizedContent} caracterizada por su búsqueda de propósito y significado`);
        variations.push(`${normalizedContent} con patrones de consumo influenciados por comunidades digitales`);
        variations.push(`${normalizedContent} que valora la exclusividad y personalización`);
        variations.push(`${normalizedContent} con alta receptividad hacia la innovación y tendencias emergentes`);
        variations.push(`${normalizedContent} caracterizada por su conciencia sobre el impacto de sus decisiones`);
        variations.push(`${normalizedContent} con expectativas de coherencia entre la experiencia online y offline`);
        variations.push(`${normalizedContent} que busca contenidos que reflejen su identidad y aspiraciones`);
        variations.push(`${normalizedContent} con un estilo de vida definido por la conectividad permanente`);
        break;
        
      case 'plataforma':
        variations.push(`${normalizedContent} aprovechando sus características únicas de distribución de contenido`);
        variations.push(`${normalizedContent} optimizando para su algoritmo de recomendación`);
        variations.push(`${normalizedContent} adaptando el formato a sus especificaciones técnicas`);
        variations.push(`${normalizedContent} considerando las mejores prácticas de engagement`);
        variations.push(`${normalizedContent} utilizando formatos nativos de alto rendimiento`);
        variations.push(`${normalizedContent} implementando estrategias específicas para su ecosistema`);
        variations.push(`${normalizedContent} con enfoque en las métricas clave de la plataforma`);
        variations.push(`${normalizedContent} aprovechando herramientas de análisis integradas`);
        variations.push(`${normalizedContent} con atención a las tendencias emergentes de la plataforma`);
        variations.push(`${normalizedContent} capitalizando sus funcionalidades de segmentación avanzada`);
        variations.push(`${normalizedContent} adaptando la estrategia a su arquitectura de interacción`);
        variations.push(`${normalizedContent} aprovechando sus capacidades de microtargeting`);
        variations.push(`${normalizedContent} maximizando el potencial de sus herramientas creativas`);
        variations.push(`${normalizedContent} con una estrategia omnicanal coordinada y coherente`);
        variations.push(`${normalizedContent} optimizando la frecuencia y timing de publicaciones`);
        variations.push(`${normalizedContent} aprovechando sus funcionalidades de monetización directa`);
        variations.push(`${normalizedContent} con un enfoque específico para su demografía predominante`);
        variations.push(`${normalizedContent} adaptando el tono comunicativo a su cultura específica`);
        variations.push(`${normalizedContent} implementando estrategias de crecimiento orgánico`);
        variations.push(`${normalizedContent} potenciando la viralidad mediante sus mecanismos nativos`);
        variations.push(`${normalizedContent} con una estrategia de contenidos adaptada a su consumo típico`);
        variations.push(`${normalizedContent} aprovechando sus capacidades de interacción en tiempo real`);
        variations.push(`${normalizedContent} optimizando la experiencia para diferentes dispositivos`);
        variations.push(`${normalizedContent} con un enfoque que favorece la retención y fidelización`);
        variations.push(`${normalizedContent} adaptando el contenido a sus formatos exclusivos`);
        variations.push(`${normalizedContent} implementando estrategias de crecimiento comunitario`);
        variations.push(`${normalizedContent} con un calendario editorial específico para sus ciclos de engagement`);
        variations.push(`${normalizedContent} aprovechando sus capacidades de conversión directa`);
        variations.push(`${normalizedContent} optimizando la estrategia para su algoritmo de distribución`);
        variations.push(`${normalizedContent} con un enfoque que maximiza su potencial de descubrimiento`);
        break;
        
      case 'tarea':
        variations.push(`${normalizedContent} siguiendo un protocolo estructurado de implementación`);
        variations.push(`${normalizedContent} dividiendo el proceso en fases claramente definidas`);
        variations.push(`${normalizedContent} estableciendo hitos de control y evaluación`);
        variations.push(`${normalizedContent} aplicando técnicas avanzadas de gestión de proyectos`);
        variations.push(`${normalizedContent} con un enfoque iterativo de mejora continua`);
        variations.push(`${normalizedContent} garantizando la calidad en cada etapa del proceso`);
        variations.push(`${normalizedContent} documentando meticulosamente cada paso del procedimiento`);
        variations.push(`${normalizedContent} con mecanismos de retroalimentación integrados`);
        variations.push(`${normalizedContent} optimizando recursos y tiempos de ejecución`);
        variations.push(`${normalizedContent} implementando metodologías ágiles para maximizar resultados`);
        variations.push(`${normalizedContent} con un sistema de priorización basado en impacto y esfuerzo`);
        variations.push(`${normalizedContent} estableciendo indicadores de progreso claros y medibles`);
        variations.push(`${normalizedContent} utilizando herramientas especializadas para cada fase`);
        variations.push(`${normalizedContent} con asignación óptima de recursos según competencias`);
        variations.push(`${normalizedContent} incorporando procesos de validación continua`);
        variations.push(`${normalizedContent} con un enfoque de entrega progresiva de valor`);
        variations.push(`${normalizedContent} estableciendo mecanismos de escalamiento ante obstáculos`);
        variations.push(`${normalizedContent} definiendo claramente roles y responsabilidades`);
        variations.push(`${normalizedContent} con un sistema de documentación y gestión del conocimiento`);
        variations.push(`${normalizedContent} implementando ciclos cortos de revisión y adaptación`);
        variations.push(`${normalizedContent} con una matriz de riesgos y planes de contingencia`);
        variations.push(`${normalizedContent} estableciendo canales efectivos de comunicación interna`);
        variations.push(`${normalizedContent} utilizando tableros de control visual para seguimiento`);
        variations.push(`${normalizedContent} con sesiones regulares de sincronización y alineamiento`);
        variations.push(`${normalizedContent} implementando prácticas de autogestión y autonomía`);
        variations.push(`${normalizedContent} definiendo criterios claros de aceptación y éxito`);
        variations.push(`${normalizedContent} con un enfoque de prototipado rápido y validación temprana`);
        variations.push(`${normalizedContent} estableciendo un repositorio centralizado de información`);
        variations.push(`${normalizedContent} utilizando sistemas de automatización donde sea posible`);
        variations.push(`${normalizedContent} con un proceso definido para gestión de cambios y adaptaciones`);
        break;
        
      case 'ejemplo':
        variations.push(`Como referencia ilustrativa, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`A modo de demostración práctica, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para visualizar la aplicación concreta, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como caso de estudio representativo, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`A título ilustrativo, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como modelo aplicativo, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para ejemplificar tangiblemente, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como muestra práctica, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para contextualizar mediante un ejemplo concreto, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como evidencia empírica, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para materializar este concepto, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`A modo de caso paradigmático, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para ilustrar de forma tangible, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como ejemplo emblemático, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`A manera de representación concreta, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para concretizar esta metodología, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como instancia representativa, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`A modo de aplicación práctica, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para demostrar la viabilidad del enfoque, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como prueba de concepto, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`A título de muestra significativa, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para ejemplificar de manera elocuente, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como caso práctico relevante, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`A manera de demostración efectiva, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para concretizar en un escenario real, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como manifestación tangible, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`A modo de implementación específica, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para visualizar mediante un caso concreto, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como prototipo funcional, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`A manera de modelo operativo, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        break;
        
      case 'resultado':
        variations.push(`${normalizedContent} con indicadores de éxito claramente definidos`);
        variations.push(`${normalizedContent} que genere un impacto cuantificable y verificable`);
        variations.push(`${normalizedContent} estableciendo nuevos estándares de excelencia`);
        variations.push(`${normalizedContent} superando significativamente las expectativas iniciales`);
        variations.push(`${normalizedContent} con beneficios tangibles a corto y largo plazo`);
        variations.push(`${normalizedContent} demostrando un retorno de inversión excepcional`);
        variations.push(`${normalizedContent} con métricas de rendimiento superiores a la media del sector`);
        variations.push(`${normalizedContent} creando ventajas competitivas sostenibles`);
        variations.push(`${normalizedContent} estableciendo un nuevo punto de referencia en la industria`);
        variations.push(`${normalizedContent} con impacto verificable en los indicadores clave de desempeño`);
        variations.push(`${normalizedContent} generando un efecto multiplicador en toda la organización`);
        variations.push(`${normalizedContent} que transforme positivamente la percepción de marca`);
        variations.push(`${normalizedContent} con beneficios que trascienden los objetivos iniciales`);
        variations.push(`${normalizedContent} produciendo un cambio paradigmático en el sector`);
        variations.push(`${normalizedContent} con resultados sostenibles y escalables`);
        variations.push(`${normalizedContent} que catalice una evolución sistemática del negocio`);
        variations.push(`${normalizedContent} generando nuevas oportunidades de crecimiento`);
        variations.push(`${normalizedContent} con un impacto mensurable en la cuenta de resultados`);
        variations.push(`${normalizedContent} estableciendo una base sólida para futuros desarrollos`);
        variations.push(`${normalizedContent} que fortalezca significativamente la posición competitiva`);
        variations.push(`${normalizedContent} con efectos positivos en múltiples dimensiones del negocio`);
        variations.push(`${normalizedContent} generando un valor diferencial difícilmente imitable`);
        variations.push(`${normalizedContent} que impulse un ciclo virtuoso de mejora continua`);
        variations.push(`${normalizedContent} con beneficios que superan exponencialmente la inversión realizada`);
        variations.push(`${normalizedContent} creando nuevos activos estratégicos para la organización`);
        variations.push(`${normalizedContent} que redefina las expectativas del mercado`);
        variations.push(`${normalizedContent} generando un impacto transformador en la cadena de valor`);
        variations.push(`${normalizedContent} con efectos positivos verificables en múltiples indicadores`);
        variations.push(`${normalizedContent} estableciendo un nuevo paradigma de eficiencia operativa`);
        variations.push(`${normalizedContent} que potencie significativamente la propuesta de valor`);
        break;
        
      case 'estilo':
        variations.push(`${normalizedContent} que refleje la identidad distintiva de la marca`);
        variations.push(`${normalizedContent} con elementos visuales cuidadosamente seleccionados`);
        variations.push(`${normalizedContent} manteniendo una coherencia absoluta en todos los aspectos`);
        variations.push(`${normalizedContent} con un equilibrio perfecto entre forma y función`);
        variations.push(`${normalizedContent} incorporando principios avanzados de diseño`);
        variations.push(`${normalizedContent} con atención meticulosa a los detalles estéticos`);
        variations.push(`${normalizedContent} siguiendo tendencias contemporáneas sin perder autenticidad`);
        variations.push(`${normalizedContent} que combine elegancia y funcionalidad`);
        variations.push(`${normalizedContent} con una paleta cromática estratégicamente definida`);
        variations.push(`${normalizedContent} que proyecte sofisticación y profesionalismo en cada elemento`);
        variations.push(`${normalizedContent} con una arquitectura visual armoniosa y equilibrada`);
        variations.push(`${normalizedContent} incorporando patrones visuales reconocibles y memorables`);
        variations.push(`${normalizedContent} con una jerarquía visual clara y efectiva`);
        variations.push(`${normalizedContent} que transmita los valores fundamentales de la marca`);
        variations.push(`${normalizedContent} con un sistema de diseño coherente y escalable`);
        variations.push(`${normalizedContent} utilizando tipografías que refuercen la identidad visual`);
        variations.push(`${normalizedContent} con un lenguaje visual distintivo y reconocible`);
        variations.push(`${normalizedContent} que equilibre innovación y familiaridad`);
        variations.push(`${normalizedContent} con elementos gráficos que potencien el mensaje`);
        variations.push(`${normalizedContent} aplicando principios de psicología del color`);
        variations.push(`${normalizedContent} con una narrativa visual consistente y envolvente`);
        variations.push(`${normalizedContent} que refleje maestría técnica y sensibilidad artística`);
        variations.push(`${normalizedContent} con un ritmo visual que guíe la atención del espectador`);
        variations.push(`${normalizedContent} implementando principios de diseño responsivo`);
        variations.push(`${normalizedContent} con una estética que trascienda modas pasajeras`);
        variations.push(`${normalizedContent} que combine tradición e innovación de forma armoniosa`);
        variations.push(`${normalizedContent} con una expresión visual auténtica y diferenciadora`);
        variations.push(`${normalizedContent} utilizando contrastes visuales para crear dinamismo`);
        variations.push(`${normalizedContent} con una estrategia visual alineada con el posicionamiento deseado`);
        variations.push(`${normalizedContent} que evoque emociones específicas a través del diseño`);
        break;
        
      case 'tono':
        variations.push(`${normalizedContent} que genere conexión emocional con la audiencia`);
        variations.push(`${normalizedContent} adaptado perfectamente al contexto comunicativo`);
        variations.push(`${normalizedContent} que transmita profesionalismo y cercanía simultáneamente`);
        variations.push(`${normalizedContent} coherente con los valores fundamentales de la marca`);
        variations.push(`${normalizedContent} que inspire confianza y credibilidad`);
        variations.push(`${normalizedContent} calibrado para resonar con las expectativas del público objetivo`);
        variations.push(`${normalizedContent} que establezca una comunicación efectiva y memorable`);
        variations.push(`${normalizedContent} con matices emocionales estratégicamente definidos`);
        variations.push(`${normalizedContent} que proyecte una personalidad distintiva y auténtica`);
        variations.push(`${normalizedContent} con una voz consistente a través de todos los canales`);
        variations.push(`${normalizedContent} que equilibre autoridad y accesibilidad`);
        variations.push(`${normalizedContent} adaptado a las particularidades culturales de la audiencia`);
        variations.push(`${normalizedContent} con una cadencia narrativa que mantenga el interés`);
        variations.push(`${normalizedContent} que evite tecnicismos innecesarios sin sacrificar precisión`);
        variations.push(`${normalizedContent} con un lenguaje inclusivo y respetuoso`);
        variations.push(`${normalizedContent} que transmita entusiasmo genuino y convicción`);
        variations.push(`${normalizedContent} con un equilibrio entre racionalidad y emotividad`);
        variations.push(`${normalizedContent} que evoque las sensaciones adecuadas en cada interacción`);
        variations.push(`${normalizedContent} adaptado a diferentes momentos del customer journey`);
        variations.push(`${normalizedContent} con una personalidad verbal claramente definida`);
        variations.push(`${normalizedContent} que refleje empatía y comprensión genuina`);
        variations.push(`${normalizedContent} con un ritmo conversacional natural y fluido`);
        variations.push(`${normalizedContent} que inspire acción sin resultar impositivo`);
        variations.push(`${normalizedContent} con matices lingüísticos que refuercen el mensaje`);
        variations.push(`${normalizedContent} que mantenga coherencia a lo largo del tiempo`);
        variations.push(`${normalizedContent} adaptado a las particularidades de cada formato`);
        variations.push(`${normalizedContent} con un equilibrio entre formalidad y calidez`);
        variations.push(`${normalizedContent} que transmita autenticidad y transparencia`);
        variations.push(`${normalizedContent} con una voz distintiva que destaque en un entorno saturado`);
        variations.push(`${normalizedContent} que refleje la evolución y madurez de la marca`);
        break;
        
      case 'opciones':
        variations.push(`Como alternativas estratégicas, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para diversificar enfoques, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como posibilidades complementarias, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para adaptarse a diferentes contextos, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como variantes tácticas, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para optimizar según recursos disponibles, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como enfoques alternativos, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para maximizar la flexibilidad de implementación, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como estrategias adaptativas, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para responder a diferentes escenarios, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como rutas de acción alternativas, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para adaptarse a distintas condiciones de mercado, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como itinerarios estratégicos complementarios, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para diversificar la cartera de aproximaciones, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como planes de contingencia, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para garantizar adaptabilidad ante imprevistos, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como soluciones modulares intercambiables, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para personalizar según necesidades específicas, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como variaciones tácticas situacionales, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para implementar según disponibilidad de recursos, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como estrategias escalonadas de implementación, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para ajustarse a diferentes presupuestos, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como aproximaciones complementarias al objetivo, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para maximizar opciones de éxito, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como caminos alternativos hacia el resultado, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para adaptarse a diferentes niveles de madurez organizacional, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como estrategias modulares combinables, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para implementar según prioridades estratégicas, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Como tácticas complementarias de optimización, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        variations.push(`Para explorar diferentes vectores de crecimiento, ${normalizedContent.charAt(0).toLowerCase() + normalizedContent.slice(1)}`);
        break;
        
      default:
        // Si no hay estrategia específica para este elemento, devolver contenido normalizado
        return [normalizedContent];
    }
    
    // Asegurar que todas las variaciones comienzan con mayúscula y aplicar mejoras RAE
    return variations.map(variation => {
      let improved = variation.charAt(0).toUpperCase() + variation.slice(1);
      improved = improveRAEPunctuation(improved);
      return improved;
    });
  };

  const improveWithAI = () => {
    setIsImproving(true);
    
    // Guardar el estado actual para el historial si es necesario
    const currentPromptToSave = improvedPrompt || prompt;
    
    setTimeout(() => {
      // Identificar qué elementos tienen contenido para mejorarlos
      const filledElements = Object.entries(promptData || {})
        .filter(([_, value]) => value?.trim())
        .map(([key]) => key);
      
      // Mejoramos cada elemento individual con variaciones aleatorias
      const improvedData = {};
      
      filledElements.forEach(key => {
        const originalContent = promptData[key];
        const elementData = racoaptereto.find(item => item.key === key);
        
        // Generar variaciones para este elemento
        const variations = generateElementVariations(key, originalContent, elementData);
        
        // Seleccionar una variación aleatoria
        improvedData[key] = variations[Math.floor(Math.random() * variations.length)];
      });
      
      // Obtener los elementos mejorados que tienen contenido
      const filledImprovedElements = Object.entries(improvedData)
        .filter(([_, value]) => value?.trim());
      
      // Generar el prompt completo con los elementos mejorados con puntuación correcta
      let fullImprovedPrompt = '';
      
      filledImprovedElements.forEach(([key, value], index) => {
        if (value.trim()) {
          // Normalizar el texto (quitar puntuación final y asegurar mayúscula inicial)
          const normalizedValue = normalizePunctuation(value);
          
          // Obtener el siguiente elemento si existe
          const nextKey = index + 1 < filledImprovedElements.length ? filledImprovedElements[index + 1][0] : null;
          const nextValue = nextKey ? filledImprovedElements[index + 1][1] : null;
          
          // Obtener el separador apropiado según el contexto
          const separator = getAppropiateSeparator(
            index, 
            filledImprovedElements.length, 
            key, 
            normalizedValue, 
            nextValue
          );
          
          // Añadir al prompt completo
          fullImprovedPrompt += normalizedValue + separator;
        }
      });
      
      // Aplicar reglas RAE al prompt completo
      fullImprovedPrompt = improveRAEPunctuation(fullImprovedPrompt);
      
      // Aplicar conectores inteligentes para mejorar la fluidez
      fullImprovedPrompt = applySmartConnectors(fullImprovedPrompt);
      
      // Mejorar la variedad léxica
      fullImprovedPrompt = improveLexicalVariety(fullImprovedPrompt);
      
      // Reemplazar palabras repetidas con sinónimos
      fullImprovedPrompt = replaceSynonyms(fullImprovedPrompt);
      
      // Añadir la versión mejorada al historial
      const newHistory = [...improvementHistory, fullImprovedPrompt.trim()];
      setImprovementHistory(newHistory);
      setCurrentVersion(newHistory.length - 1);
      
      setImprovedPrompt(fullImprovedPrompt.trim());
      setIsImproving(false);
    }, 1500);
  };

  // Función para navegar en el historial de mejoras
  const navigateHistory = (direction) => {
    if (direction === 'prev' && currentVersion > 0) {
      setCurrentVersion(currentVersion - 1);
      setImprovedPrompt(improvementHistory[currentVersion - 1]);
    } else if (direction === 'next' && currentVersion < improvementHistory.length - 1) {
      setCurrentVersion(currentVersion + 1);
      setImprovedPrompt(improvementHistory[currentVersion + 1]);
    } else if (direction === 'original') {
      setCurrentVersion(0);
      setImprovedPrompt('');
      setImprovementHistory([prompt]);
    }
  };

  // Inicializar historial si está vacío y hay un prompt
  useEffect(() => {
    if (improvementHistory.length === 0 && prompt) {
      setImprovementHistory([prompt]);
      setCurrentVersion(0);
    }
  }, [prompt, improvementHistory.length]);

  // Visualización de mejoras por elemento
  const renderElementImprovements = () => {
    if (!improvedPrompt || Object.keys(improvedElements).length === 0) return null;
    
    return (
      <div className="mt-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
          Mejoras por elemento:
        </h3>
        <div className="space-y-3">
          {Object.entries(improvedElements).map(([key, value]) => {
            const elementData = racoaptereto.find(item => item.key === key);
            if (!elementData || !value) return null;
            
            return (
              <div key={key} className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-1">
                  <div className="w-6 h-6 bg-gradient-to-br from-primary-blue to-primary-pink text-white rounded-lg flex items-center justify-center font-bold text-xs">
                    {elementData.letter}
                  </div>
                  <h4 className="font-medium text-gray-800 dark:text-gray-200">
                    {elementData.title}:
                  </h4>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 pl-8">
                  {value}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const displayPrompt = improvedPrompt || prompt;

  // Función para abrir el selector de LLM
  const handleOpenLLMSelector = () => {
    setShowLLMSelector(true);
    copyToClipboardWithPrefix(displayPrompt);
  };

  return (
    <motion.div
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      transition={{duration: 0.6}}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-8 w-full overflow-hidden"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          <span className="italic">Prompt</span> generado
          {improvedPrompt && (
            <span className="ml-2 text-sm bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
              Mejorado con IA {improvementHistory.length > 1 ? `(versión ${currentVersion} de ${improvementHistory.length - 1})` : ''}
            </span>
          )}
        </h2>
        
        <div className="flex items-center space-x-2">
          <motion.button
            onClick={improveWithAI}
            disabled={isImproving}
            className="flex items-center space-x-2 bg-[#F54CC8] text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all"
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
          >
            <SafeIcon icon={GiMagicWand} className={`w-5 h-5 ${isImproving ? 'animate-spin' : ''}`} />
            <span>{isImproving ? 'Mejorando...' : 'Mejorar con IA local'}</span>
          </motion.button>
        </div>
      </div>
      
      <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 mb-6 w-full overflow-auto">
        <p className="text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
          {displayPrompt}
        </p>
      </div>
      
      {/* Controles de historial de versiones */}
      {improvementHistory.length > 1 && (
        <div className="flex items-center justify-center space-x-2 mb-6">
          <motion.button
            onClick={() => navigateHistory('original')}
            disabled={currentVersion === 0}
            className={`px-3 py-1 rounded-md text-sm ${
              currentVersion === 0
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
            whileHover={currentVersion !== 0 ? {scale: 1.05} : {}}
            whileTap={currentVersion !== 0 ? {scale: 0.95} : {}}
          >
            Original
          </motion.button>
          
          <motion.button
            onClick={() => navigateHistory('prev')}
            disabled={currentVersion <= 0}
            className={`p-1 rounded-md ${
              currentVersion <= 0
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
            whileHover={currentVersion > 0 ? {scale: 1.05} : {}}
            whileTap={currentVersion > 0 ? {scale: 0.95} : {}}
          >
            <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
          </motion.button>
          
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {currentVersion === 0 ? "Original" : `Versión ${currentVersion} de ${improvementHistory.length - 1}`}
          </span>
          
          <motion.button
            onClick={() => navigateHistory('next')}
            disabled={currentVersion >= improvementHistory.length - 1}
            className={`p-1 rounded-md ${
              currentVersion >= improvementHistory.length - 1
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
            whileHover={currentVersion < improvementHistory.length - 1 ? {scale: 1.05} : {}}
            whileTap={currentVersion < improvementHistory.length - 1 ? {scale: 0.95} : {}}
          >
            <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
          </motion.button>
        </div>
      )}
      
      {/* Mostrar mejoras por elemento */}
      {renderElementImprovements()}
      
      <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
        <div className="flex flex-wrap items-center gap-2">
          <motion.button
            onClick={() => copyToClipboard(displayPrompt)}
            className="flex items-center space-x-2 bg-primary-blue text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all"
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
          >
            <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4" />
            <span>{copied ? '¡Copiado!' : 'Copiar'}</span>
          </motion.button>
          
          <motion.button
            onClick={() => sharePrompt(displayPrompt)}
            className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all"
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
          >
            <SafeIcon icon={FiShare2} className="w-4 h-4" />
            <span>Compartir</span>
          </motion.button>
          
          <motion.button
            onClick={onEdit}
            className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all"
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
          >
            <SafeIcon icon={FiEdit3} className="w-4 h-4" />
            <span>Editar</span>
          </motion.button>
          
          {improvedPrompt && currentVersion > 0 && (
            <motion.button
              onClick={() => navigateHistory('original')}
              className="flex items-center space-x-2 bg-yellow-500 text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all"
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
            >
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
              <span>Original</span>
            </motion.button>
          )}
          
          <motion.button
            onClick={handleOpenLLMSelector}
            className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all"
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
          >
            <SafeIcon icon={llmCopied ? FiCheck : FaIcons.FaRobot} className="w-4 h-4" />
            <span>{llmCopied ? '¡Copiado para LLM!' : 'Mejorar con LLM (externos)'}</span>
          </motion.button>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">¿Te gusta el resultado?</span>
          <motion.button
            onClick={() => {
              setRating('good');
              sendFeedback(true);
            }}
            className={`p-2 rounded-lg transition-colors ${
              rating === 'good'
                ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}
            whileHover={{scale: 1.1}}
            whileTap={{scale: 0.9}}
          >
            <SafeIcon icon={FiThumbsUp} className="w-5 h-5" />
          </motion.button>
          
          <motion.button
            onClick={() => {
              setRating('bad');
              sendFeedback(false);
            }}
            className={`p-2 rounded-lg transition-colors ${
              rating === 'bad'
                ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}
            whileHover={{scale: 1.1}}
            whileTap={{scale: 0.9}}
          >
            <SafeIcon icon={FiThumbsDown} className="w-5 h-5" />
          </motion.button>
        </div>
      </div>
      
      {feedbackSent && (
        <motion.div
          initial={{opacity: 0, height: 0}}
          animate={{opacity: 1, height: 'auto'}}
          className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 rounded-r-lg"
        >
          <p className="text-sm text-blue-800 dark:text-blue-300">
            {feedbackMessage}
          </p>
        </motion.div>
      )}
      
      {rating === 'bad' && !feedbackSent && (
        <motion.div
          initial={{opacity: 0, height: 0}}
          animate={{opacity: 1, height: 'auto'}}
          className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 rounded-r-lg"
        >
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            Gracias por tu retroalimentación. Intenta ser más específico en los campos <span className="italic">InnovaPrompt</span> para obtener mejores resultados.
          </p>
        </motion.div>
      )}
      
      {/* Modal de Selector de LLM */}
      <AnimatePresence>
        {showLLMSelector && (
          <LLMSelector onClose={() => setShowLLMSelector(false)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PromptDisplay;